import type { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { isNativePlatform } from './platform.js';

/**
 * Native-only persistence for the refresh token, backed by the Android
 * Keystore via @aparajita/capacitor-secure-storage. On web every function
 * is a no-op returning null/void — the refresh token lives in the HTTP-only
 * cookie there and never touches JS.
 *
 * The plugin module is imported lazily so the web bundle never ships it.
 */
const REFRESH_TOKEN_KEY = 'refreshToken';

/**
 * Load the plugin module. NEVER return the `SecureStorage` proxy itself from
 * an async function: Capacitor plugin proxies intercept every property
 * access — including the `.then` probe promise resolution performs — which
 * the bridge turns into a native "SecureStorage.then()" call that throws
 * "not implemented" and leaves the awaiting caller hung (found live on the
 * emulator at MT.7). Resolving with the module namespace object is safe.
 */
async function securePluginModule(): Promise<{ SecureStorage: typeof SecureStorage }> {
  return import('@aparajita/capacitor-secure-storage');
}

export async function getStoredRefreshToken(): Promise<string | null> {
  if (!isNativePlatform()) return null;
  try {
    const { SecureStorage: storage } = await securePluginModule();
    const value = await storage.get(REFRESH_TOKEN_KEY);
    return typeof value === 'string' && value.length > 0 ? value : null;
  } catch {
    // Unreadable Keystore entry — treat as logged out; password login recovers.
    return null;
  }
}

export async function setStoredRefreshToken(token: string): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SecureStorage: storage } = await securePluginModule();
    await storage.set(REFRESH_TOKEN_KEY, token);
  } catch (err) {
    // Non-fatal: the session still works in memory; the user just has to
    // log in with their password again on the next cold start.
    console.warn('Failed to persist refresh token to secure storage', err);
  }
}

export async function clearStoredRefreshToken(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SecureStorage: storage } = await securePluginModule();
    await storage.remove(REFRESH_TOKEN_KEY);
  } catch (err) {
    console.warn('Failed to clear refresh token from secure storage', err);
  }
}

/**
 * Body for POST /auth/refresh and /auth/logout: `{ refreshToken }` on native
 * (read from the Keystore), undefined on web (cookie transport, no body).
 */
export async function nativeRefreshBody(): Promise<{ refreshToken: string } | undefined> {
  if (!isNativePlatform()) return undefined;
  const token = await getStoredRefreshToken();
  return token ? { refreshToken: token } : undefined;
}
